import axios from 'axios';
import { assetUrl } from '@/lib/assetUrl';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Vehicle APIs
export const vehicleApi = {
  list: (params = {}) => axios.get(`${API}/vehicles/`, { params }),
  listPublic: (params = {}) => axios.get(`${API}/vehicles/public`, { params }),
  get: (id) => axios.get(`${API}/vehicles/${id}`),
  create: (data) => axios.post(`${API}/vehicles/`, data),
  update: (id, data) => axios.put(`${API}/vehicles/${id}`, data),
  delete: (id) => axios.delete(`${API}/vehicles/${id}`),
  getBrands: (vehicleType) => axios.get(`${API}/vehicles/brands`, { params: vehicleType ? { vehicle_type: vehicleType } : {} }),
  getFilters: () => axios.get(`${API}/vehicles/filters`),
  revealPrice: (data) => axios.post(`${API}/vehicles/reveal-price`, data),
  trackView: (id) => axios.post(`${API}/vehicles/${id}/view`),
  rate: (id, data) => axios.post(`${API}/vehicles/${id}/rate`, data),
  getRatings: (id) => axios.get(`${API}/vehicles/${id}/ratings`),
  getRelated: (id) => axios.get(`${API}/vehicles/${id}/related`),
  getMostViewed: (limit = 6) => axios.get(`${API}/vehicles/stats/most-viewed?limit=${limit}`),
  getTrending: (limit = 6) => axios.get(`${API}/vehicles/stats/trending?limit=${limit}`),
  compare: (ids) => axios.post(`${API}/vehicles/compare`, ids),
  getPriceHistory: (id) => axios.get(`${API}/vehicles/${id}/price-history`),
  getSentiment: (id) => axios.get(`${API}/vehicles/${id}/sentiment`),
  // Price Alerts
  getMyAlerts: () => axios.get(`${API}/vehicles/my-alerts`),
  createPriceAlert: (id, data) => axios.post(`${API}/vehicles/${id}/price-alert`, data),
  cancelPriceAlert: (alertId) => axios.delete(`${API}/vehicles/price-alert/${alertId}`),
  // API Import
  apiSearch: (params) => axios.get(`${API}/vehicles/api-search`, { params }),
  apiImport: (data) => axios.post(`${API}/vehicles/api-import`, data),
};

// Coupon APIs
export const couponApi = {
  list: (params = {}) => axios.get(`${API}/coupons/`, { params }),
  get: (id) => axios.get(`${API}/coupons/${id}`),
  claim: (id, data) => axios.post(`${API}/coupons/claim/${id}`, data),
  create: (data) => axios.post(`${API}/coupons/`, data),
  update: (id, data) => axios.put(`${API}/coupons/${id}`, data),
  delete: (id) => axios.delete(`${API}/coupons/${id}`),
  getCategories: () => axios.get(`${API}/coupons/categories`),
  trackView: (id) => axios.post(`${API}/coupons/${id}/view`).catch(() => {}),
  rate: (id, data) => axios.post(`${API}/coupons/${id}/rate`, data),
  getRatings: (id) => axios.get(`${API}/coupons/${id}/ratings`),
  getRelated: (id) => axios.get(`${API}/coupons/${id}/related`),
  getMostViewed: (limit = 6) => axios.get(`${API}/coupons/stats/most-viewed?limit=${limit}`),
};

// News APIs
export const newsApi = {
  list: (params = {}) => axios.get(`${API}/news/`, { params }),
  latest: (site = 'auto', limit = 6) => axios.get(`${API}/news/latest?site=${site}&limit=${limit}`),
  sync: (site = 'auto') => axios.post(`${API}/news/sync?site=${site}`),
};

// Deals APIs (MetroMandi syndication)
export const dealsApi = {
  list: (params = {}) => axios.get(`${API}/deals/`, { params }),
  latest: (limit = 8) => axios.get(`${API}/deals/latest?limit=${limit}`),
  sync: () => axios.post(`${API}/deals/sync`),
  categories: () => axios.get(`${API}/deals/categories`),
  stores: () => axios.get(`${API}/deals/stores`),
  create: (data) => axios.post(`${API}/deals/create`, data),
  update: (id, data) => axios.put(`${API}/deals/${id}`, data),
  delete: (id) => axios.delete(`${API}/deals/${id}`),
};

// Lead APIs
export const leadApi = {
  create: (data) => axios.post(`${API}/leads/`, data),
  capture: (data) => axios.post(`${API}/leads/`, data),
  list: (params = {}) => axios.get(`${API}/leads/`, { params }),
  stats: () => axios.get(`${API}/leads/stats`),
  updateStatus: (id, status) => axios.put(`${API}/leads/${id}/status?status=${status}`),
  updateTags: (id, tags) => axios.put(`${API}/leads/${id}/tags`, tags),
  delete: (id) => axios.delete(`${API}/leads/${id}`),
  bulkAction: (action, leadIds, newStatus) =>
    axios.post(`${API}/leads/bulk-action?action=${action}${newStatus ? `&new_status=${newStatus}` : ''}`, leadIds),
  uploadCsv: (file, source = 'csv_upload', sheets = '') => {
    const fd = new FormData();
    fd.append('file', file);
    const params = new URLSearchParams({ source });
    if (sheets) params.set('sheets', sheets);
    return axios.post(`${API}/leads/upload-csv?${params.toString()}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  detectSheets: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/leads/detect-sheets`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  analytics: () => axios.get(`${API}/leads/analytics`),
  filterOptions: () => axios.get(`${API}/leads/filter-options`),
  // List Cleaner
  startClean: (file, sheets = '', industry = 'others') => {
    const fd = new FormData();
    fd.append('file', file);
    const params = new URLSearchParams();
    if (sheets) params.set('sheets', sheets);
    if (industry) params.set('industry', industry);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return axios.post(`${API}/leads/clean${qs}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getCleanJob: (jobId) => axios.get(`${API}/leads/clean/${jobId}`),
  exportCleaned: (jobId) => `${API}/leads/clean/${jobId}/export`,
  importCleaned: (jobId, source = 'media_buying') => axios.post(`${API}/leads/clean/${jobId}/import?source=${encodeURIComponent(source)}`),
  getImportStatus: (jobId) => axios.get(`${API}/leads/clean/${jobId}/import-status`),
  listCleanJobs: (limit = 20) => axios.get(`${API}/leads/clean-jobs?limit=${limit}`),
  deleteCleanJob: (jobId) => axios.delete(`${API}/leads/clean/${jobId}`),
  // Blacklist
  listBlacklist: (params = {}) => axios.get(`${API}/leads/blacklist`, { params }),
  addBlacklist: (data) => axios.post(`${API}/leads/blacklist`, data),
  removeBlacklist: (id) => axios.delete(`${API}/leads/blacklist/${id}`),
  importBlacklist: (file, type = 'email') => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/leads/blacklist/import?type=${type}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  blacklistStats: () => axios.get(`${API}/leads/blacklist/stats`),
  uploadLogs: (limit = 20) => axios.get(`${API}/leads/upload-logs?limit=${limit}`),
  exportFiltered: (params = {}) => `${API}/leads/export?${new URLSearchParams(params).toString()}`,
  // B2B API Keys
  listApiKeys: () => axios.get(`${API}/leads/api-keys`),
  createApiKey: (name) => axios.post(`${API}/leads/api-keys?name=${encodeURIComponent(name)}`),
  revokeApiKey: (id) => axios.delete(`${API}/leads/api-keys/${id}`),
};

// Delivery Client APIs
export const deliveryApi = {
  listClients: () => axios.get(`${API}/delivery/clients`),
  createClient: (data) => axios.post(`${API}/delivery/clients`, data),
  createFromTemplate: (template) => axios.post(`${API}/delivery/clients/from-template?template=${template}`),
  getClient: (id) => axios.get(`${API}/delivery/clients/${id}`),
  updateClient: (id, data) => axios.put(`${API}/delivery/clients/${id}`, data),
  deleteClient: (id) => axios.delete(`${API}/delivery/clients/${id}`),
  toggleClient: (id) => axios.put(`${API}/delivery/clients/${id}/toggle`),
  pushLeads: (clientId, leadIds) => axios.post(`${API}/delivery/push/${clientId}`, leadIds),
  pushFiltered: (clientId, params) => axios.post(`${API}/delivery/push-filtered/${clientId}`, null, { params }),
  testDelivery: (clientId) => axios.post(`${API}/delivery/test/${clientId}`),
  getLogs: (params = {}) => axios.get(`${API}/delivery/logs`, { params }),
  getStats: () => axios.get(`${API}/delivery/stats`),
};

// Ad Sync APIs (Meta/Google)
export const adSyncApi = {
  listConfigs: () => axios.get(`${API}/ad-sync/configs`),
  createConfig: (data) => axios.post(`${API}/ad-sync/configs`, data),
  updateConfig: (id, data) => axios.put(`${API}/ad-sync/configs/${id}`, data),
  deleteConfig: (id) => axios.delete(`${API}/ad-sync/configs/${id}`),
  triggerMetaSync: (configId) => axios.post(`${API}/ad-sync/sync/meta`, null, { params: configId ? { config_id: configId } : {} }),
  triggerGoogleSync: (configId) => axios.post(`${API}/ad-sync/sync/google`, null, { params: configId ? { config_id: configId } : {} }),
  getSyncLogs: (params = {}) => axios.get(`${API}/ad-sync/logs`, { params }),
};

// Affiliate APIs
export const affiliateApi = {
  getMe: () => axios.get(`${API}/affiliates/me`),
  getMyLeads: (params = {}) => axios.get(`${API}/affiliates/me/leads`, { params }),
  getMyStats: () => axios.get(`${API}/affiliates/me/stats`),
  list: (params = {}) => axios.get(`${API}/affiliates/`, { params }),
  create: (data) => axios.post(`${API}/affiliates/`, data),
  update: (id, data) => axios.put(`${API}/affiliates/${id}`, data),
  // Public application
  apply: (data) => axios.post(`${API}/affiliates/apply`, data),
  // Admin application management
  getApplications: (params = {}) => axios.get(`${API}/affiliates/applications`, { params }),
  getApplicationStats: () => axios.get(`${API}/affiliates/applications/stats`),
  getApplication: (id) => axios.get(`${API}/affiliates/applications/${id}`),
  approveApplication: (id) => axios.put(`${API}/affiliates/applications/${id}/approve`),
  rejectApplication: (id) => axios.put(`${API}/affiliates/applications/${id}/reject`),
  bulkImport: (file) => { const fd = new FormData(); fd.append('file', file); return axios.post(`${API}/affiliates/bulk-import`, fd); },
};

// Campaign APIs
export const campaignApi = {
  list: (params = {}) => axios.get(`${API}/campaigns/`, { params }),
  create: (data) => axios.post(`${API}/campaigns/`, data),
};

// User Vehicle Submission APIs
export const submissionApi = {
  submit: (data) => axios.post(`${API}/submissions/`, data),
  getMySubmissions: () => axios.get(`${API}/submissions/my`),
  getMyLeads: () => axios.get(`${API}/submissions/my-leads`),
  update: (id, data) => axios.put(`${API}/submissions/${id}`, data),
  delete: (id) => axios.delete(`${API}/submissions/${id}`),
  // Admin
  getQueue: (params = {}) => axios.get(`${API}/submissions/admin/queue`, { params }),
  moderate: (id, data) => axios.put(`${API}/submissions/admin/${id}/moderate`, data),
  getStats: () => axios.get(`${API}/submissions/admin/stats`),
};

// Recharge APIs
export const rechargeApi = {
  // Public
  listOffers: () => axios.get(`${API}/recharge/offers`),
  submitClaim: (data) => axios.post(`${API}/recharge/claims`, data),
  uploadProof: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/recharge/upload-proof`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  submitKyc: (claimId, data) => axios.put(`${API}/recharge/claims/${claimId}/kyc`, data),
  myClaims: (phone) => axios.get(`${API}/recharge/my-claims`, { params: { phone } }),
  // Admin
  createOffer: (data) => axios.post(`${API}/recharge/offers`, data),
  updateOffer: (id, data) => axios.put(`${API}/recharge/offers/${id}`, data),
  deleteOffer: (id) => axios.delete(`${API}/recharge/offers/${id}`),
  listClaims: (params = {}) => axios.get(`${API}/recharge/claims`, { params }),
  verifyClaim: (id, data) => axios.put(`${API}/recharge/claims/${id}/verify`, data),
  markRecharged: (id) => axios.put(`${API}/recharge/claims/${id}/mark-recharged`, {}),
};

// Admin APIs
export const adminApi = {
  getDashboard: () => axios.get(`${API}/admin/dashboard`),
  getUsers: (params = {}) => axios.get(`${API}/admin/users`, { params }),
  seedData: () => axios.post(`${API}/seed-data`),
  scrapeCoupons: (categories) => {
    const params = categories ? { categories: categories.join(',') } : {};
    return axios.post(`${API}/admin/scrape-coupons`, null, { params });
  },
  getScrapeLogs: (params = {}) => axios.get(`${API}/admin/scrape-logs`, { params }),
  getUTMAnalytics: () => axios.get(`${API}/admin/utm-analytics`),
  // Auto-Populate
  triggerVehiclePopulate: () => axios.post(`${API}/admin/auto-populate/vehicles`),
  triggerCouponPopulate: () => axios.post(`${API}/admin/auto-populate/coupons`),
  getAutoPopulateLogs: (limit = 20) => axios.get(`${API}/admin/auto-populate/logs?limit=${limit}`),
  // Brand Partners
  getBrandPartners: () => axios.get(`${API}/admin/brand-partners`),
  createBrandPartner: (data) => axios.post(`${API}/admin/brand-partners`, data),
  updateBrandPartner: (id, data) => axios.put(`${API}/admin/brand-partners/${id}`, data),
  deleteBrandPartner: (id) => axios.delete(`${API}/admin/brand-partners/${id}`),
  // Coupon Import
  importCouponsFromUrl: (data) => axios.post(`${API}/admin/import-coupons`, data),
  importCouponsCSV: (file) => { const fd = new FormData(); fd.append('file', file); return axios.post(`${API}/admin/import-coupons/bulk`, fd); },
  getCouponImportJobs: () => axios.get(`${API}/admin/import-coupons/jobs`),
  // LinkMyDeals + CueLinks daily coupon sync
  syncCouponsNow: () => axios.post(`${API}/admin/sync-coupons-now`),
  getCouponSyncLogs: (limit = 20) => axios.get(`${API}/admin/coupon-sync-logs?limit=${limit}`),
};

// Auth APIs
export const authApi = {
  login: (data) => axios.post(`${API}/auth/login`, data),
  register: (data) => axios.post(`${API}/auth/register`, data),
  forgotPassword: (email) => axios.post(`${API}/auth/forgot-password`, { email }),
  resetPassword: (token, newPassword) => axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword }),
  getMe: () => axios.get(`${API}/auth/me`),
};

// Upload APIs
export const uploadApi = {
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/upload/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadImages: (files) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    return axios.post(`${API}/upload/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Ads APIs
export const adsApi = {
  // Admin CRUD
  create: (data) => axios.post(`${API}/ads`, data),
  list: (params) => axios.get(`${API}/ads`, { params }),
  update: (id, data) => axios.put(`${API}/ads/${id}`, data),
  remove: (id) => axios.delete(`${API}/ads/${id}`),
  uploadCreative: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/ads/upload-creative`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  generateAi: (data) => axios.post(`${API}/ads/generate-ai`, data),
  // Analytics
  getAnalytics: () => axios.get(`${API}/ads/analytics`),
  // Public
  serve: (site, placement, limit = 5) => axios.get(`${API}/ads/serve?site=${site}&placement=${placement}&limit=${limit}`),
  trackImpression: (id) => axios.post(`${API}/ads/${id}/impression`),
  trackClick: (id) => axios.post(`${API}/ads/${id}/click`),
};

// Payout APIs
export const payoutApi = {
  getPending: () => axios.get(`${API}/affiliates/payouts/pending`),
  create: (data) => axios.post(`${API}/affiliates/payouts`, data),
  getHistory: (skip = 0, limit = 50) => axios.get(`${API}/affiliates/payouts/history?skip=${skip}&limit=${limit}`),
  getMyPayouts: () => axios.get(`${API}/affiliates/my-payouts`),
};

// Export APIs
export const exportApi = {
  leads: () => `${API}/export/leads`,
  ads: () => `${API}/export/ads`,
  affiliates: () => `${API}/export/affiliates`,
};

// Chat APIs
export const chatApi = {
  sendMessage: (data) => axios.post(`${API}/chat/message`, data),
  getHistory: (sessionId) => axios.get(`${API}/chat/history/${sessionId}`),
  saveUserInfo: (sessionId, data) => axios.post(`${API}/chat/user-info/${sessionId}`, data),
};

// Push Notification APIs
export const notificationApi = {
  subscribe: (data) => axios.post(`${API}/notifications/subscribe`, data),
  unsubscribe: (endpoint) => axios.post(`${API}/notifications/unsubscribe`, { endpoint }),
  getVapidKey: () => axios.get(`${API}/notifications/vapid-key`),
};

// Format price in INR
export const formatPrice = (price) => {
  if (price === null || price === undefined || price === 0) {
    return 'Price on Request';
  }
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(price);
};

// Format number in Indian style
export const formatNumber = (num) => {
  return new Intl.NumberFormat('en-IN').format(num);
};

// Validate Indian phone number
export const validateIndianPhone = (phone) => {
  const pattern = /^(\+91)?[6-9]\d{9}$/;
  const cleaned = phone.replace(/[\s-]/g, '');
  return pattern.test(cleaned);
};

// Get backend URL for images
export const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http') || path.startsWith('/uploads/') || path.startsWith('/api/')) return assetUrl(path);
  // Object storage path — serve via /api/files/ endpoint, under the site's own address
  return assetUrl(`/api/files/${path}`);
};
